import React from 'react';
import { Table, Card, Statistic, DatePicker, Tabs, Tag, Row, Col, Spin, Alert, Empty } from 'antd';
import { ShoppingCartOutlined, DollarOutlined, TeamOutlined, RiseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import SwitchShop from '../shop/switch-shop';
import { getCurrentShopId } from '../shop/get-current-shop';

const { MonthPicker } = DatePicker;

// 从URL获取查询参数
const getQueryParam = (key) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
};

const ReportPage = () => {
    const [loading, setLoading] = React.useState(false);
    const [month, setMonth] = React.useState(dayjs());
    const [data, setData] = React.useState(null);
    const [shopId, setShopId] = React.useState(null);
    const [error, setError] = React.useState(null);

    // 初始化：优先从URL参数取shopId，再降级到SafeStorage
    React.useEffect(() => {
        const urlShopId = getQueryParam('shopId');
        if (urlShopId) {
            setShopId(urlShopId);
        } else {
            getCurrentShopId().then(id => setShopId(id));
        }
    }, []);

    // 监听店铺切换事件
    React.useEffect(() => {
        const onShopSwitch = () => {
            const urlShopId = getQueryParam('shopId');
            if (urlShopId) {
                setShopId(urlShopId);
            } else {
                getCurrentShopId().then(id => setShopId(id));
            }
        };
        window.addEventListener('shopSwitch', onShopSwitch);
        return () => window.removeEventListener('shopSwitch', onShopSwitch);
    }, []);

    const fetchReport = async (selectedMonth, currentShopId) => {
        if (!currentShopId) return;
        setLoading(true);
        setError(null);
        try {
            const m = selectedMonth.format('YYYY-MM');
            const res = await fetch(`/api/report/monthly?month=${m}&shopId=${currentShopId}`);
            const result = await res.json();
            if (result.success) {
                setData(result.data);
            } else {
                setData(null);
                setError(result.data || '获取报表失败');
            }
        } catch (err) {
            console.error('报表获取失败:', err);
            setData(null);
            setError('网络请求失败');
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (shopId) fetchReport(month, shopId);
    }, [shopId, month]);

    const onMonthChange = (date) => {
        if (date) setMonth(date);
    };

    // ====== 分销员表格列 ======
    const shareColumns = [
        {
            title: '排名',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: '姓名',
            dataIndex: 'name',
            width: 100,
        },
        {
            title: '手机号',
            dataIndex: 'phoneNo',
            width: 120,
        },
        {
            title: '所属店铺',
            dataIndex: 'shopName',
            width: 140,
        },
        {
            title: '分销等级',
            dataIndex: 'levelName',
            width: 100,
            render: (text) => <Tag>{text || '未设置'}</Tag>,
        },
        {
            title: '订单数',
            dataIndex: 'orderCount',
            width: 80,
            sorter: (a, b) => a.orderCount - b.orderCount,
        },
        {
            title: '销售额(元)',
            dataIndex: 'salesAmountYuan',
            width: 120,
            sorter: (a, b) => a.salesAmountYuan - b.salesAmountYuan,
            render: (val) => <span className="text-green-600 font-semibold">¥{val?.toFixed(2) ?? '0.00'}</span>,
        },
        {
            title: '佣金(元)',
            dataIndex: 'commissionYuan',
            width: 120,
            sorter: (a, b) => a.commissionYuan - b.commissionYuan,
            render: (val) => <span className="text-orange-600 font-semibold">¥{val?.toFixed(2) ?? '0.00'}</span>,
        },
    ];

    // ====== 导购员表格列 ======
    const guideColumns = [
        {
            title: '排名',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: '姓名',
            dataIndex: 'name',
            width: 100,
        },
        {
            title: '手机号',
            dataIndex: 'phoneNo',
            width: 120,
        },
        {
            title: '所属店铺',
            dataIndex: 'shopName',
            width: 200,
        },
        {
            title: '订单数',
            dataIndex: 'orderCount',
            width: 80,
            sorter: (a, b) => a.orderCount - b.orderCount,
        },
        {
            title: '销售额(元)',
            dataIndex: 'salesAmountYuan',
            width: 120,
            sorter: (a, b) => a.salesAmountYuan - b.salesAmountYuan,
            render: (val) => <span className="text-green-600 font-semibold">¥{val?.toFixed(2) ?? '0.00'}</span>,
        },
        {
            title: '备注',
            dataIndex: 'remark',
            render: (text) => text && <span className="text-gray-400 text-xs">{text}</span>,
        },
    ];

    const summary = data?.shareSummary;
    const guideSummary = data?.guideSummary;

    return (
        <div className="space-y-4">
            <SwitchShop />

            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="admin-page-title mb-0">月度业绩报表</h2>
                <MonthPicker
                    value={month}
                    onChange={onMonthChange}
                    allowClear={false}
                    placeholder="选择月份"
                    style={{ width: 160 }}
                />
            </div>

            {error && (
                <Alert message={error} type="error" showIcon closable />
            )}

            <Spin spinning={loading}>
                {/* 即使数据为空也展示汇总卡片 */}
                <Row gutter={[12, 12]}>
                    <Col xs={12} sm={6}>
                        <Card size="small" className="shadow-sm">
                            <Statistic
                                title="分销员销售额"
                                value={summary?.totalSalesYuan || 0}
                                precision={2}
                                prefix={<RiseOutlined />}
                                suffix="元"
                                valueStyle={{ color: '#10b981', fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small" className="shadow-sm">
                            <Statistic
                                title="分销员佣金"
                                value={summary?.totalCommissionYuan || 0}
                                precision={2}
                                prefix={<DollarOutlined />}
                                suffix="元"
                                valueStyle={{ color: '#f59e0b', fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small" className="shadow-sm">
                            <Statistic
                                title="分销员活跃人数"
                                value={summary?.activeCount || 0}
                                prefix={<TeamOutlined />}
                                suffix="人"
                                valueStyle={{ fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small" className="shadow-sm">
                            <Statistic
                                title="导购员销售额"
                                value={guideSummary?.totalSalesYuan || 0}
                                precision={2}
                                prefix={<ShoppingCartOutlined />}
                                suffix="元"
                                valueStyle={{ color: '#10b981', fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card
                    size="small"
                    title={
                        <span>
                            <span className="mr-3">{month.format('YYYY年MM月')} 详细数据</span>
                            <Tag color="blue">分销员 {summary?.activeCount || 0} 人</Tag>
                            <Tag color="green">导购员 {guideSummary?.activeCount || 0} 人</Tag>
                        </span>
                    }
                >
                    {!shopId ? (
                        <Empty description="请先选择店铺" />
                    ) : data === null && !loading ? (
                        <Empty description={error || '暂无数据'} />
                    ) : (
                        <Tabs defaultActiveKey="share">
                            <Tabs.TabPane
                                tab={<span><TeamOutlined /> 分销员明细</span>}
                                key="share"
                            >
                                {data?.shareUsers?.length > 0 ? (
                                    <Table
                                        size="small"
                                        rowKey="customerId"
                                        columns={shareColumns}
                                        dataSource={data.shareUsers}
                                        pagination={{ pageSize: 20, simple: true }}
                                        scroll={{ x: 'max-content' }}
                                    />
                                ) : (
                                    <Alert
                                        message="本月暂无分销员业绩数据"
                                        description="可能原因：该店铺本月没有分销员订单，或分销员尚未产生佣金"
                                        type="info"
                                        showIcon
                                    />
                                )}
                            </Tabs.TabPane>
                            <Tabs.TabPane
                                tab={<span><ShoppingCartOutlined /> 导购员明细</span>}
                                key="guide"
                            >
                                {data?.guides?.length > 0 ? (
                                    <Table
                                        size="small"
                                        rowKey="userId"
                                        columns={guideColumns}
                                        dataSource={data.guides}
                                        pagination={{ pageSize: 20, simple: true }}
                                        scroll={{ x: 'max-content' }}
                                    />
                                ) : (
                                    <Alert
                                        message="本月暂无导购员业绩数据"
                                        description="导购员需要订单中设置了「导购ID」(shareGuideId) 才会出现在报表中"
                                        type="info"
                                        showIcon
                                    />
                                )}
                            </Tabs.TabPane>
                        </Tabs>
                    )}
                </Card>
            </Spin>
        </div>
    );
};

export default ReportPage;
